import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import RoleCarousel from './RoleCarousel';
import { useCountUp, formatNumber } from '../hooks/useCountUp';
import './Hero.css';

const HERO_VIDEO_URL = 'https://cdn.just.ac/promo/avatar-preview.mp4';
const DEMO_VIDEO_URL = 'https://cdn.just.ac/promo/PerformerAI%20%D0%B4%D0%B5%D0%BC%D0%BE%D0%BD%D1%81%D1%82%D1%80%D0%B0%D1%86%D0%B8%D1%8F%20%D0%B0%D0%B2%D0%B0%D1%82%D0%B0%D1%80%D0%BE%D0%B2.mp4';
const TELEGRAM_URL = 'https://t.me/justdisplay';

const Hero = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const popupVideoRef = useRef(null);
  const dialogsCount = useCountUp(62000, inView, 2200);
  const successRate = useCountUp(93, inView, 1800);

  useEffect(() => {
    if (isVideoOpen && popupVideoRef.current) {
      popupVideoRef.current.currentTime = 0;
      popupVideoRef.current.play();
    }
  }, [isVideoOpen]);

  return (
    <>
      <section className="hero" id="hero" ref={ref}>
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        <div className="hero-container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: [0.6, -0.05, 0.01, 0.99] }}
          >
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Цифровой аватар с искусственным интеллектом
            </motion.h1>
            
            <motion.div
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <RoleCarousel />
            </motion.div>

            <motion.div
              className="hero-cta"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="hero-cta-row">
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="cta-button cta-button-primary cta-button-buy">
                  Купить за{' '}
                  <span className="price-old">600&nbsp;000&nbsp;₽</span>{' '}
                  <span className="price-new">350&nbsp;000&nbsp;₽</span>
                </a>
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="cta-button cta-button-primary">
                  Арендовать 20&nbsp;000&nbsp;₽/день
                </a>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-video-area"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.4, ease: [0.6, -0.05, 0.01, 0.99] }}
          >
            <video
              className="hero-video-bg"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={HERO_VIDEO_URL} type="video/mp4" />
            </video>
            <button
              className="hero-demo-btn"
              onClick={() => setIsVideoOpen(true)}
              type="button"
            >
              Посмотреть демонстрацию
            </button>
          </motion.div>
        </div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="hero-stat">
            <span className="hero-stat-value">{formatNumber(dialogsCount)}+</span>
            <span className="hero-stat-label">Диалогов с клиентами</span>
          </div>
          <div className="hero-stat">
            <span className="hero-stat-value">{successRate}%</span>
            <span className="hero-stat-label">
              В {successRate}% случаев наши аватары справляются без помощи живых сотрудников
            </span>
          </div>
        </motion.div>
      </section>

      <AnimatePresence>
        {isVideoOpen && (
          <>
            <motion.div
              className="hero-video-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsVideoOpen(false)}
            />
            <div className="hero-video-modal-wrapper">
              <motion.div
                className="hero-video-modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                <button
                  className="hero-video-modal-close"
                  onClick={() => setIsVideoOpen(false)}
                  type="button"
                  aria-label="Закрыть"
                >
                  ×
                </button>
                <video
                  ref={popupVideoRef}
                  className="hero-video-popup"
                  controls
                  playsInline
                >
                  <source src={DEMO_VIDEO_URL} type="video/mp4" />
                </video>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Hero;
